import { iconTrash } from "@kensho/icons";
import { useLogger } from "@kensho/lumberjack";
import { Button } from "@kensho/neo";
import clsx from "clsx";
import { use } from "react";

import { prettyModified } from "../../core/historical-transcripts/historical-transcripts-table/util/pretty-modified";
import { TranscriptPermissionsContext } from "../../core/transcription/transcript-permissions-provider";
import { useDispatchEditorAction } from "../../core/transcription/transcript/dispatch-editor-action-provider";
import { APITranscript } from "../../types/types";
import { getUserColor } from "../../utils/get-user-color";
import { produceEditorAction } from "../../utils/transcript-revision-utils";
import { NeedsReviewHighlight } from "../types";
import { HEIGHT } from "./constants";

interface NeedsReviewDetailBoxProps {
  transcript: APITranscript;
  version: number;
  active?: boolean;
  index: number;
  total: number;
  needsReviewHighlight: NeedsReviewHighlight;
  setActiveHighlightId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  top: number;
}

export function NeedsReviewDetailBox({
  active = false,
  index,
  total,
  transcript,
  version,
  setActiveHighlightId,
  needsReviewHighlight,
  top,
}: NeedsReviewDetailBoxProps) {
  const { transcriptPermissions } = use(TranscriptPermissionsContext);
  const { dispatchEditorAction } = useDispatchEditorAction();
  const log = useLogger();

  if (!needsReviewHighlight) return null;
  const { annotation } = needsReviewHighlight;
  if (!annotation) return null;
  const { range } = needsReviewHighlight;
  const { segments } = range;
  const { createdOn, createdBy } = annotation;
  if (!createdBy || !createdOn || !segments) return null;
  const color = getUserColor(createdBy);
  const text = (() => {
    const fullText = needsReviewHighlight.text;
    const maxLength = 100;
    return fullText.length >= maxLength
      ? `${fullText.slice(0, Math.floor(maxLength / 2))}…${fullText.slice(-Math.floor(maxLength / 2))}`
      : fullText;
  })();
  return (
    <div
      style={{
        top: `${top}px`,
        height: HEIGHT,
        transition: "top 0.3s ease",
      }}
      className={clsx(
        "absolute w-full rounded-lg border border-gray-200 p-4 text-sm",
        active ? "bg-white shadow-lg" : "bg-blue-50",
      )}
      onClick={() => setActiveHighlightId(annotation.id)}
      onKeyDown={() => setActiveHighlightId(annotation.id)}
    >
      <div className="flex h-full flex-col justify-around">
        <div className="flex items-center gap-2">
          {/* // TODO: replace with UserPresence component */}
          <div
            style={{ backgroundColor: color.fill, borderColor: color.border }}
            className={clsx(
              "flex h-8 w-8 items-center justify-center rounded-full border text-sm",
            )}
          >
            {createdBy[0].toUpperCase()}
          </div>
          <div className="flex flex-col">
            <div>{createdBy}</div>
            <div className="text-gray-500">{prettyModified(createdOn)}</div>
          </div>
        </div>
        <div className="border-l-2 border-yellow-400 pl-2 break-all">
          {text}
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {total > 1 && `${index + 1} out of ${total}`}
          </div>
          <Button
            disabled={!transcriptPermissions.edit}
            size="small"
            icon={iconTrash}
            onClick={() => {
              const editorAction = produceEditorAction({
                action: {
                  type: "delete-annotation",
                  annotationId: annotation.id,
                },
                transcript,
                transcriptSelection: null,
                log,
              });
              if (!editorAction) return;
              dispatchEditorAction(editorAction, version);
              setActiveHighlightId(undefined);
            }}
          >
            Remove highlight
          </Button>
        </div>
      </div>
    </div>
  );
}
